/* eslint-disable @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument */
import { Component, forwardRef, Input } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { SearchFilterMetadataModel } from "../../../models/search-filter-metadata.model";
import {SearchFilterAttributeModel} from "../../../models/search-filter-attribute.model";
import {GoogleMapService} from "../google-map/google-map.service";

@Component({
    selector: "pcg-ng-location-filter",
    templateUrl: "./location-filter.component.html",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LocationFilterComponent),
            multi: true
        }
    ]
})
export class LocationFilterComponent implements ControlValueAccessor {
    @Input() public templateId: string;
    @Input() public searchFilters: SearchFilterMetadataModel;

    protected expandedFilters: Array<string> = [];
    protected selectedValues: Array<string> = [];

    public searchText: string = "";
    public filteredAttributes: { [key: string]: Array<SearchFilterAttributeModel> } = {};

    private onChange: (value: any) => void;
    private onTouched: () => void;

    public constructor(
        public googleMapService: GoogleMapService
    ) { }

    protected getFiltersForSection(id: string): Array<SearchFilterAttributeModel> {
        return this.searchFilters.attributes;
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public writeValue(obj: Array<string>): void {
        this.selectedValues = obj || [];

        this.expandedFilters = this.searchFilters.attributes
            ?.filter(f => f.nestedAttributes.some(a => this.selectedValues?.includes(a.id)))
            .map(f => f.name);

        this.searchFilters.attributes.forEach(filter => {
            this.filteredAttributes[filter.name] = filter.nestedAttributes;
        });
    }

    protected applySearch(filter: SearchFilterAttributeModel): void {
        const searchTextLower = this.searchText.trim().toLowerCase();
        this.filteredAttributes[filter.name] = filter.nestedAttributes.filter(attribute =>
            attribute.name.toLowerCase().includes(searchTextLower)
        );
    }

    protected clearSearch(): void {
        this.searchText = "";
        this.searchFilters.attributes.forEach(filter => {
            this.filteredAttributes[filter.name] = filter.nestedAttributes;
        });

        this.clearFilters();
    }

    protected toggleCheckbox(value: string): void {
        const index = this.selectedValues.indexOf(value);

        if (index > -1) {
            this.selectedValues.splice(index, 1);
        } else {
            this.selectedValues.push(value);
        }

        if (this.onChange) {
            this.onChange(this.selectedValues);
        }
    }

    protected isFilterExpanded(filter: SearchFilterAttributeModel): boolean {
        return this.expandedFilters.includes(filter.name);
    }

    protected hasSelectedFilterAttributes(filter: SearchFilterAttributeModel): boolean {
        return filter.nestedAttributes.some(attribute => this.selectedValues?.includes(attribute.id));
    }

    protected getSelectedFilterAttributes(filter: SearchFilterAttributeModel): string {
        return filter.nestedAttributes
            .filter(attribute => this.selectedValues?.includes(attribute.id))
            .map(attribute => attribute.name)
            .join(", ");
    }

    protected getSelectedFilterAttributesCount(filter: SearchFilterAttributeModel): number {
        return filter.nestedAttributes
            .filter(attribute => this.selectedValues?.includes(attribute.id))
            .length;
    }

    protected toggleFilter(filter: SearchFilterAttributeModel): void {
        if (this.isFilterExpanded(filter)) {
            this.expandedFilters = this.expandedFilters.filter(f => f !== filter.name);
        } else {
            this.expandedFilters.push(filter.name);
        }
    }

    protected clearFilters(): void {
        this.writeValue([]);
        this.onChange(this.selectedValues);
    }

    public scrollToViewport(targetElement: HTMLElement): void {
        const scrollableContainer = targetElement.parentElement;

        const containerRect = scrollableContainer.getBoundingClientRect();
        const elementRect = targetElement.getBoundingClientRect();

        if (elementRect.top < containerRect.top) {
            scrollableContainer.scrollTop -= (containerRect.top - elementRect.top);
        } else if (elementRect.bottom > containerRect.bottom) {
            scrollableContainer.scrollTop += (elementRect.bottom - containerRect.bottom);
        }
    }
}
